<script setup lang="ts">
import { computed, onMounted, type Ref, ref, watch, provide } from 'vue'
import { storeToRefs } from 'pinia'
import PMessage from 'primevue/message'
import PProgressSpinner from 'primevue/progressspinner'
import PChart from 'primevue/chart'
import { FilterMatchMode } from '@primevue/core/api'
import CDashboardClassroomResultsCourseTableTaskStatus from '@/presentation/components/dashboard/CDashboardClassroomResultsCourseTableTaskStatus.vue'
import type { ChartOptions } from 'chart.js'
import { EvaluationRange } from '@/core/domain/resources/enums/EvaluationRange.ts'
import { useToast } from 'primevue/usetoast'
import { useNabooContent } from '@/assets/composables/useNabooContent.ts'
import { useProfileStore } from '@/presentation/stores/profile/profile.ts'
import { useDashboardClassroomResultStore } from '@/presentation/stores/dashboard/dashboardClassroomResult.ts'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import CDashboardClassroomResultsCourseTable from '@/presentation/components/dashboard/CDashboardClassroomResultsCourseTable.vue'
import type { DashboardClassroomModuleResult } from '@/core/domain/dashboard/DashboardClassroomModuleResult'
import { useDashboardPerformance } from '@/presentation/composables/useDashboardPerformance'

// Import Primevue services
const toast = useToast()
const { openModuleContent } = useNabooContent()

const props = defineProps<{
  title: string
  schoolId: number
  classroomId: number
  moduleId: number
  courseId: number
  moduleResults: DashboardClassroomModuleResult
}>()

// STORES
const { getFullName } = storeToRefs(useProfileStore())
const dashboardClassroomResultStore = useDashboardClassroomResultStore()
const dashboardClassroomStore = useDashboardClassroomStore()
const { scoreModule, dashboardClassroomResultStoreLoading } = storeToRefs(
  dashboardClassroomResultStore
)
const { dashboardSelectedClassroom } = storeToRefs(dashboardClassroomStore)

// LOCAL STATE
const loading = ref(false)
const error = ref<string | null>(null)
const dataInitialized = ref(false)
const tableKey = ref(0) // Pour forcer le rechargement de la table

// METHODS
const { setDashboardSelectedClassroomById } = useDashboardClassroomStore()

/**
 * Charge les données du module
 */
const loadModuleData = async (forceRefresh = false) => {
  // Si déjà initialisé et pas d'erreur ni rechargement forcé, ne pas recharger
  if (dataInitialized.value && !error.value && !forceRefresh) return

  try {
    loading.value = true
    error.value = null

    // S'assurer que la classe est correctement sélectionnée
    if (
      !dashboardSelectedClassroom.value ||
      dashboardSelectedClassroom.value.schoolClassroomId !== props.classroomId
    ) {
      await setDashboardSelectedClassroomById(props.schoolId, props.classroomId)
    }

    // Les données sont maintenant reçues via props, pas besoin de les charger

    // Initialiser le filtre de l'enseignant
    filters.value.teacherFullName.value = teacherOptn.value?.value ?? null

    // Incrémenter la clé pour forcer le rechargement du tableau
    tableKey.value++

    // Marquer comme initialisé après le chargement réussi
    dataInitialized.value = true
  } catch (err) {
    console.error('Erreur lors du chargement des détails de la thématique:', err)
    error.value =
      err instanceof Error ? err.message : 'Impossible de charger les détails de la thématique'

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: error.value,
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

// Les résultats du module sont maintenant reçus via props
// const moduleResults est maintenant props.moduleResults

// Observer les changements de props
watch(
  [() => props.schoolId, () => props.classroomId, () => props.moduleId, () => props.moduleResults],
  () => {
    // Réinitialiser quand les props changent
    dataInitialized.value = true
  }
)

onMounted(async () => {
  await loadModuleData()
})

// Use performance composable for optimizations
const { authInfo } = useDashboardPerformance()
provide('authInfo', authInfo.value)

// Source de données transformée
const dataSource = computed(() => {
  return props.moduleResults.scores || []
})

// Options d'enseignants pour le filtre
const teachersOptions = computed(() => {
  const teachers = [
    ...new Set(
      dataSource.value
        ?.filter((student) => student.teacherFullName)
        ?.map((student) => student.teacherFullName)
    )
  ]?.map((teacher) => {
    return {
      label: teacher,
      value: teacher
    }
  })

  return teachers ? [{ label: 'Tous les enseignants', value: null }, ...teachers] : []
})

// Option d'enseignant par défaut basée sur l'utilisateur connecté
const teacherOptn = computed(() => {
  return teachersOptions.value.find((teacher) => teacher.value === getFullName.value)
})

// Configuration des filtres de table avec valeurs par défaut améliorées
const filters: Ref<{
  global: { value: string | null; matchMode: string }
  firstname: { value: string | null; matchMode: string }
  lastname: { value: string | null; matchMode: string }
  teacherFullName: { value: string | null; matchMode: string }
}> = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  firstname: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  lastname: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  teacherFullName: { value: null, matchMode: FilterMatchMode.EQUALS }
})

// Options du graphique optimisées
const chartOptions: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true,
      callbacks: {
        // Formater les tooltips pour plus de clarté
        label: (context) => {
          const labels = ['Non commencé', 'Défi', 'Efforts à faire', 'Force']
          const value = context.parsed.y
          return `${labels[context.dataIndex]}: ${value} élève${value > 1 ? 's' : ''}`
        }
      }
    },
    datalabels: {
      anchor: 'end',
      align: 'end',
      color: '#000',
      font: {
        size: 12,
        weight: 'bold'
      },
      // Only display values if > 0
      formatter: (value: any) => (value > 0 ? value.toString() : ''),
      display: (context: any) => (context.dataset.data[context.dataIndex] as number) > 0
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#495057',
        font: {
          size: 14
        }
      },
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      ticks: {
        color: '#495057',
        font: {
          size: 14
        },
        stepSize: 1
      },
      grid: {
        color: '#E5E5E5'
      }
    }
  },
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 30,
      bottom: 10
    }
  }
}

// Transformation des données pour le graphique avec gestion d'erreur améliorée
const transformDataForChart = computed(() => {
  const data = dataSource.value
  const result = {
    notStarted: 0,
    defi: 0,
    effortsAFaire: 0,
    force: 0
  }

  // Comptage des élèves par catégorie
  data.forEach((student) => {
    // Vérifier si toutes les sessions sont terminées
    const allSessionsCompleted = student.learningGrains?.every(
      (grain) => grain.postQuizEvalRangeName !== null
    )

    // Si toutes les sessions sont terminées, catégoriser selon moduleEvalRangeName
    if (allSessionsCompleted) {
      if (student.moduleEvalRangeName === EvaluationRange.Values.DEFI) {
        result.defi += 1
      } else if (student.moduleEvalRangeName === EvaluationRange.Values.EFFORT_A_FAIRE) {
        result.effortsAFaire += 1
      } else if (student.moduleEvalRangeName === EvaluationRange.Values.FORCE) {
        result.force += 1
      }
    } else {
      // Module en cours ou non commencé
      result.notStarted += 1
    }
  })

  return {
    labels: ['Non commencé', 'Défi', 'Efforts à faire', 'Force'],
    datasets: [
      {
        label: 'Répartition des élèves',
        backgroundColor: ['#999', '#FF6F61', '#FBA86F', '#32CD32'],
        data: [result.notStarted, result.defi, result.effortsAFaire, result.force]
      }
    ]
  }
})

// Déterminer si nous sommes en cours de chargement
const isLoading = computed(() => {
  return loading.value || dashboardClassroomResultStoreLoading.value.fetchScoreModule
})

// Déterminer s'il n'y a pas de données
const isEmpty = computed(() => {
  return !isLoading.value && (!dataSource.value || dataSource.value.length === 0)
})

// Déterminer si on peut exporter les données
const canExportData = computed(() => {
  return dataSource.value.length > 0
})

// Préparer les données pour l'export
const exportCSV = () => {
  if (!canExportData.value) return

  try {
    const exportData = dataSource.value.map((student) => ({
      Prénom: student.firstname,
      Nom: student.lastname,
      Enseignant: student.teacherFullName || '-',
      'Score diagnostic (%)': student.diagScorePercentRounded,
      'Score module (%)': student.moduleScorePercentRounded,
      Variation: student.diagnosticModuleVariation
    }))

    // Convertir en CSV
    const header = Object.keys(exportData[0]).join(',')
    const rows = exportData.map((row) => Object.values(row).join(','))
    const csv = [header, ...rows].join('\n')

    // Télécharger
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `progression-${props.title.replace(/\s+/g, '-')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.add({
      severity: 'success',
      summary: 'Export réussi',
      detail: 'Les données ont été exportées avec succès',
      life: 3000
    })
  } catch (err) {
    console.error("Erreur lors de l'export CSV:", err)
    toast.add({
      severity: 'error',
      summary: "Erreur d'export",
      detail: "Une erreur est survenue lors de l'export des données",
      life: 5000
    })
  }
}
</script>

<template>
  <c-base-block
    :title="title"
    accordion
    :key="`module-${props.moduleId}-${props.taskId}`"
    class="dashboard-progress-block"
    toggleable
  >
    <div class="mb-4 flex flex-wrap items-center justify-between">
      <a class="bo-active-color cursor-pointer" @click="openModuleContent(moduleId, courseId)">
        Découvrir les contenus du module <i class="pi pi-external-link" />
      </a>

      <button
        v-if="canExportData"
        class="p-button p-button-outlined p-button-sm"
        @click="exportCSV"
      >
        <i class="pi pi-download mr-1"></i> Exporter CSV
      </button>
    </div>

    <!-- Affichage en cas d'erreur -->
    <p-message v-if="error" severity="error" class="mb-4 w-full">
      {{ error }}
    </p-message>

    <!-- Affichage en cas de chargement -->
    <div v-else-if="isLoading" class="flex items-center justify-center p-8">
      <p-progress-spinner
        style="width: 50px; height: 50px"
        strokeWidth="5"
        fill="#EEEEEE"
        animationDuration=".5s"
      />
    </div>

    <!-- Cas où il n'y a pas de données -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center rounded-md bg-gray-50 p-6"
    >
      <i class="pi pi-chart-bar text-surface-500 dark:text-surface-300 mb-4 text-4xl"></i>
      <h3 class="mt-0 mb-2">Aucune donnée disponible</h3>
      <p class="text-surface-700 dark:text-surface-100 text-center">
        Aucun résultat n'est disponible pour cette thématique actuellement.
      </p>
    </div>

    <!-- Contenu principal -->
    <div
      v-else
      class="border-surface-300 col-span-12 mt-6 flex flex-col gap-6 rounded-2xl border p-6"
    >
      <!-- Section graphique résumé -->
      <section class="progress-summary">
        <h4 class="text-surface-800 mb-4 text-2xl font-bold">
          Répartition des élèves par intervalles de scores
        </h4>
        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4">
            <div class="chart-container" style="position: relative; height: 240px">
              <p-chart
                class="h-full w-full"
                type="bar"
                :data="transformDataForChart"
                :options="chartOptions"
              />
            </div>
          </div>
          <div class="col-span-12 flex flex-col justify-center md:col-span-6">
            <div class="stats-summary rounded-lg bg-gray-50 p-4">
              <h5 class="m-0 mb-2 text-lg font-medium">Synthèse</h5>
              <ul class="m-0 list-none p-0">
                <li
                  v-for="(count, index) in transformDataForChart.datasets[0].data"
                  :key="index"
                  class="mb-2 flex items-center"
                >
                  <div
                    class="stat-indicator mr-2"
                    :style="{
                      backgroundColor: transformDataForChart.datasets[0].backgroundColor[index]
                    }"
                  ></div>
                  <span class="font-medium">{{ transformDataForChart.labels[index] }}:</span>
                  <span class="ml-2">{{ count }} élève{{ count > 1 ? 's' : '' }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <!-- Section détails par élève -->
      <section class="student-details">
        <h4 class="text-surface-800 mb-4 text-2xl font-bold">Détails par élève</h4>

        <!-- Légende -->
        <div class="bg-surface-50 col-span-12 mb-4 grid-cols-12 items-center gap-4 rounded-2xl p-6">
          <h5 class="text-surface-800 col-span-12 mb-4 text-xl font-bold">Légende</h5>
          <div class="flex flex-wrap gap-6">
            <div class="col-12 flex items-center gap-2">
              <CDashboardClassroomResultsCourseTableTaskStatus :grain-eval-range="null" />
              <p class="m-0">Session non réalisée</p>
            </div>
            <div class="flex items-center gap-2">
              <CDashboardClassroomResultsCourseTableTaskStatus
                :grain-eval-range="EvaluationRange.Values.FORCE"
              />
              <p class="m-0"><span class="font-bold">Force : </span>score &gt; 70%</p>
            </div>
            <div class="flex items-center gap-2">
              <CDashboardClassroomResultsCourseTableTaskStatus
                :grain-eval-range="EvaluationRange.Values.EFFORT_A_FAIRE"
              />
              <p class="m-0">
                <span class="font-bold">Efforts à faire : </span>score entre 40% et 70%
              </p>
            </div>
            <div class="flex items-center gap-2">
              <CDashboardClassroomResultsCourseTableTaskStatus
                :grain-eval-range="EvaluationRange.Values.DEFI"
              />
              <p class="m-0"><span class="font-bold">Défi : </span>score &lt; 40%</p>
            </div>
          </div>
        </div>

        <!-- Tableau de données -->
        <CDashboardClassroomResultsCourseTable
          :data-source="dataSource"
          :teachers-options="teachersOptions"
          :is-loading="isLoading"
        />
      </section>
    </div>
  </c-base-block>
</template>

<style scoped>
.chart-container {
  min-height: 240px;
  width: 100%;
}

.stat-indicator {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.progress-summary,
.student-details {
  margin-bottom: 1.5rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .chart-container {
    height: 200px;
  }
}
</style>
